import { Flipper, Flipped } from 'react-flip-toolkit';
import Fade from '@mui/material/Fade';
import { Stack } from '@mui/material';

import { InfiniteScroll } from '@/components/infinite-scroll/InfiniteScroll';
import { createSubgroupColorGenerator } from '@/modules/group/utils/createSubgroupColourGenerator';
import { nonFalsey } from 'Utils/nonFalsey';
import { graphql, type DocumentType } from '@/gql';
import { Post } from '@/modules/group/noticeboard/post-collection/Post';

export const MULTI_GROUPS_POST_FRAGMENT = graphql(`
  fragment multiGroupsPostFragment on Post {
    _id
    created
    content
    editorContent
    htmlContent
    authorContext
    author {
      ...authorData
    }
    subgroupStr
    subgroup {
      _id
    }
    pinned
    title

    hasPostLinkedObjectsConnection {
      edges {
        node {
          _id
          targetType
          target {
            ... on PostTag {
              _id
              displayName
            }
            ... on Event {
              _id
            }
          }
        }
      }
    }

    owner {
      _id
      name
      iconUrl
      userCanPost
      userCanDeleteAllPosts
      userCanEditAllPosts
      hasKsg
      machineName
      committees {
        _id
        name
      }
    }

    notificationsConnection {
      edges {
        node {
          _id
          targetType
          targetName
          target {
            ... on Group {
              _id
            }
            ... on User {
              ...authorData
            }
          }
        }
      }
    }

    reactionsConnection {
      edges {
        node {
          _id
          reaction
          skinTone
          userContext
          time
          user {
            ...authorData
          }
        }
      }
    }

    commentsConnection(orderBy: [CREATED_DESC]) {
      edges {
        node {
          _id
          created
          content
          editorContent
          htmlContent
          authorContext
          author {
            ...authorData
          }
        }
      }
    }
  }
`);

interface Props {
  posts: Array<DocumentType<typeof MULTI_GROUPS_POST_FRAGMENT>>;
  hasMorePosts: boolean;
  loading: boolean;
  loadMorePosts: () => void;
  serviceMachineName?: string | null;
}

export const MultiGroupPostCollection = ({
  posts,
  hasMorePosts,
  loadMorePosts,
  loading,
  serviceMachineName = null,
}: Props) => {
  const flipKey = posts.map((post) => String(post && post._id)).join(':');

  return (
    <div>
      <InfiniteScroll
        load={loadMorePosts}
        hasMore={hasMorePosts}
        isLoading={loading}
      >
        <Flipper flipKey={flipKey}>
          <Stack spacing={3}>
            {posts
              .map((post) => {
                const postId = post._id;
                if (!postId) {
                  return null;
                }

                const group = post.owner;

                if (!group) {
                  return null;
                }

                const { committees } = group;

                const getSubgroupColor = createSubgroupColorGenerator(
                  committees.filter(nonFalsey),
                );

                return (
                  <Flipped key={post._id || undefined} flipId={post._id || ''}>
                    <div>
                      <Fade in={true}>
                        <div>
                          <Post
                            group={group}
                            post={post}
                            getSubgroupColor={getSubgroupColor}
                            showOwner={true}
                            or2Variant="inner"
                            serviceMachineName={serviceMachineName}
                            location={serviceMachineName ? 'adminHub' : 'myHub'}
                          />
                        </div>
                      </Fade>
                    </div>
                  </Flipped>
                );
              })
              .filter(nonFalsey)}
          </Stack>
        </Flipper>
      </InfiniteScroll>
    </div>
  );
};
